import { create } from 'zustand'
import type {
  LoadState,
  PlanItem,
  PlanWithDetails,
  SchedulingAssignment,
  SchedulingStatus,
} from '@/types'
import { api, describeError } from '@/services/api'
import { moveItem } from '@/lib/utils'

interface PlanState {
  status: LoadState
  error: string | null
  plan: PlanWithDetails | null
  /** Item currently being dragged in the order of service. */
  draggingItemId: string | null
  saving: boolean

  load: (planId: string) => Promise<void>
  clear: () => void

  addItem: (input: Partial<PlanItem>) => Promise<void>
  updateItem: (itemId: string, patch: Partial<PlanItem>) => Promise<void>
  deleteItem: (itemId: string) => Promise<void>
  /** Optimistically reorders, then persists the new running order. */
  reorderItems: (fromIndex: number, toIndex: number) => Promise<void>
  setDragging: (itemId: string | null) => void

  /**
   * Lays the rota out from the plan's service type and folds the result in.
   *
   * Deliberately NOT a `load()`: that flips status to 'loading', which
   * unmounts the plan's tabs and drops you back on the running order — you
   * press a button on the Teams tab and get thrown off it.
   */
  fillTeams: () => Promise<void>
  /** Adds one empty slot to this plan — a position the service type does not
      normally cover, or a second of one it does. */
  addAssignment: (positionId: string) => Promise<void>
  /** Removes one slot from this plan. The position still exists on the team. */
  removeAssignment: (assignmentId: string) => Promise<void>
  setAssignmentStatus: (assignmentId: string, status: SchedulingStatus) => Promise<void>
  setAssignmentPerson: (assignmentId: string, personId: string | null) => Promise<void>
  updatePlan: (patch: Partial<PlanWithDetails>) => Promise<void>
}

function totalOf(items: PlanItem[]): number {
  return items.reduce((sum, item) => sum + item.durationSeconds, 0)
}

export const usePlanStore = create<PlanState>((set, get) => ({
  status: 'idle',
  error: null,
  plan: null,
  draggingItemId: null,
  saving: false,

  async load(planId) {
    set({ status: 'loading', error: null })
    try {
      const plan = await api.get<PlanWithDetails>(`/plans/${planId}`)
      set({ plan, status: 'success' })
    } catch (error) {
      set({ status: 'error', error: describeError(error), plan: null })
    }
  },

  clear() {
    set({ plan: null, status: 'idle', error: null, draggingItemId: null })
  },

  async addItem(input) {
    const plan = get().plan
    if (!plan) return
    set({ saving: true })
    try {
      const item = await api.post<PlanItem>(`/plans/${plan.id}/items`, input)
      const items = [...plan.items, item].sort((a, b) => a.sortOrder - b.sortOrder)
      set({ plan: { ...plan, items, totalDurationSeconds: totalOf(items) }, error: null })
    } catch (error) {
      set({ error: describeError(error) })
    } finally {
      set({ saving: false })
    }
  },

  async updateItem(itemId, patch) {
    const plan = get().plan
    if (!plan) return
    // Optimistic — the editor stays responsive while IndexedDB catches up.
    const optimistic = plan.items.map((i) => (i.id === itemId ? { ...i, ...patch } : i))
    set({ plan: { ...plan, items: optimistic, totalDurationSeconds: totalOf(optimistic) } })
    try {
      const saved = await api.patch<PlanItem>(`/plan-items/${itemId}`, patch)
      const current = get().plan
      if (!current) return
      const items = current.items.map((i) => (i.id === itemId ? saved : i))
      set({ plan: { ...current, items, totalDurationSeconds: totalOf(items) }, error: null })
    } catch (error) {
      set({ error: describeError(error) })
      await get().load(plan.id)
    }
  },

  async deleteItem(itemId) {
    const plan = get().plan
    if (!plan) return
    const items = plan.items.filter((i) => i.id !== itemId)
    set({ plan: { ...plan, items, totalDurationSeconds: totalOf(items) } })
    try {
      await api.delete(`/plan-items/${itemId}`)
    } catch (error) {
      set({ error: describeError(error) })
      await get().load(plan.id)
    }
  },

  async reorderItems(fromIndex, toIndex) {
    const plan = get().plan
    if (!plan || fromIndex === toIndex) return
    const reordered = moveItem(plan.items, fromIndex, toIndex).map((item, index) => ({
      ...item,
      sortOrder: index + 1,
    }))
    set({ plan: { ...plan, items: reordered } })
    try {
      await api.post(`/plans/${plan.id}/items/reorder`, { itemIds: reordered.map((i) => i.id) })
    } catch (error) {
      set({ error: describeError(error) })
      await get().load(plan.id)
    }
  },

  setDragging(itemId) {
    set({ draggingItemId: itemId })
  },

  async fillTeams() {
    const plan = get().plan
    if (!plan) return
    set({ saving: true, error: null })
    try {
      const result = await api.post<{ created: number; assignments: SchedulingAssignment[] }>(
        `/plans/${plan.id}/teams`,
        {},
      )
      const current = get().plan
      if (!current) return
      set({ plan: { ...current, assignments: result.assignments }, error: null })
    } catch (error) {
      set({ error: describeError(error) })
      throw error
    } finally {
      set({ saving: false })
    }
  },

  async addAssignment(positionId) {
    const plan = get().plan
    if (!plan) return
    try {
      const created = await api.post<SchedulingAssignment>('/assignments', {
        planId: plan.id,
        positionId,
      })
      const current = get().plan
      if (!current) return
      set({ plan: { ...current, assignments: [...current.assignments, created] }, error: null })
    } catch (error) {
      set({ error: describeError(error) })
    }
  },

  async removeAssignment(assignmentId) {
    const plan = get().plan
    if (!plan) return
    try {
      await api.delete(`/assignments/${assignmentId}`)
      const current = get().plan
      if (!current) return
      set({
        plan: { ...current, assignments: current.assignments.filter((a) => a.id !== assignmentId) },
        error: null,
      })
    } catch (error) {
      set({ error: describeError(error) })
    }
  },

  async setAssignmentStatus(assignmentId, status) {
    const plan = get().plan
    if (!plan) return
    try {
      const saved = await api.patch<SchedulingAssignment>(`/assignments/${assignmentId}`, { status })
      const current = get().plan
      if (!current) return
      set({
        plan: {
          ...current,
          assignments: current.assignments.map((a) => (a.id === assignmentId ? saved : a)),
        },
        error: null,
      })
    } catch (error) {
      set({ error: describeError(error) })
    }
  },

  async setAssignmentPerson(assignmentId, personId) {
    const plan = get().plan
    if (!plan) return
    try {
      /*
       * ONLY the person. The status is the server's to decide — it is the
       * side that knows whether the service type schedules people outright,
       * and sending `status: 'pending'` here is what stopped "Schedule people
       * automatically" from ever working.
       */
      const saved = await api.patch<SchedulingAssignment>(`/assignments/${assignmentId}`, {
        personId,
      })
      const current = get().plan
      if (!current) return
      set({
        plan: {
          ...current,
          assignments: current.assignments.map((a) => (a.id === assignmentId ? saved : a)),
        },
        error: null,
      })
    } catch (error) {
      set({ error: describeError(error) })
    }
  },

  async updatePlan(patch) {
    const plan = get().plan
    if (!plan) return
    set({ plan: { ...plan, ...patch } })
    try {
      await api.patch(`/plans/${plan.id}`, patch)
    } catch (error) {
      set({ error: describeError(error) })
      await get().load(plan.id)
    }
  },
}))
